/**
 * Button — the canonical primary action in the app.
 *
 * Variants:
 *   • primary   — solid brand green, the default
 *   • secondary — quiet outline, brand-tinted
 *   • ghost     — transparent, for nav / tertiary actions
 *   • accent    — saffron CTA (used sparingly for "Save now" energy)
 *   • danger    — destructive
 *   • link      — looks like an anchor
 *
 * Sizes: sm | md (default) | lg
 *
 * Polymorphic `as` prop so we can render <Link> without losing styling.
 */
import React from 'react';
import { css } from './tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'accent' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  /** Render as a different element (e.g. <Link>). */
  as?: React.ElementType;
}

const SIZE_STYLES: Record<ButtonSize, React.CSSProperties> = {
  sm: { padding: '7px 12px', fontSize: 13, gap: 6, minHeight: 32, borderRadius: 10 },
  md: { padding: '10px 16px', fontSize: 14, gap: 8, minHeight: 40, borderRadius: 12 },
  lg: { padding: '13px 22px', fontSize: 15, gap: 10, minHeight: 48, borderRadius: 14 },
};

function getVariantStyles(variant: ButtonVariant): React.CSSProperties {
  switch (variant) {
    case 'primary':
      return {
        background: css.color.primary,
        color: css.color.textOnPrimary,
        border: `1px solid ${css.color.primary}`,
        boxShadow: css.shadow.brand,
      };
    case 'secondary':
      return {
        background: 'transparent',
        color: css.color.primary,
        border: `1.5px solid ${css.color.primary}`,
      };
    case 'ghost':
      return {
        background: 'transparent',
        color: css.color.text,
        border: '1px solid transparent',
      };
    case 'accent':
      return {
        background: css.color.accent,
        color: css.color.textOnAccent,
        border: `1px solid ${css.color.accent}`,
        boxShadow: css.shadow.accent,
      };
    case 'danger':
      return {
        background: css.color.danger,
        color: '#FFFFFF',
        border: `1px solid ${css.color.danger}`,
      };
    case 'link':
      return {
        background: 'transparent',
        color: css.color.primary,
        border: '1px solid transparent',
        padding: 0,
        minHeight: 'auto',
        textDecoration: 'none',
      };
  }
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth,
    loading,
    leadingIcon,
    trailingIcon,
    as: Tag = 'button',
    disabled,
    children,
    style,
    onClick,
    type,
    className,
    ...rest
  },
  ref,
) {
  const variantStyle = getVariantStyles(variant);
  const sizeStyle = SIZE_STYLES[size];
  // Link variant ignores size padding
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: css.font.body,
    fontWeight: 600,
    letterSpacing: 0,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.6 : 1,
    transition:
      'transform 80ms ease, box-shadow 160ms ease, background 160ms ease, color 160ms ease',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    width: fullWidth ? '100%' : undefined,
    ...sizeStyle,
    ...(variant === 'link' ? { padding: 0, minHeight: 'auto' } : {}),
    ...variantStyle,
    ...style,
  };

  // <button> defaults to type="submit" inside <form>; force button.
  const buttonType = Tag === 'button' ? (type ?? 'button') : undefined;

  return (
    <Tag
      ref={ref}
      type={buttonType}
      disabled={disabled || loading}
      onClick={onClick}
      style={baseStyle}
      className={['pm-btn', `pm-btn--${variant}`, `pm-btn--${size}`, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden
          style={{
            width: 14,
            height: 14,
            borderRadius: 999,
            border: `2px solid currentColor`,
            borderRightColor: 'transparent',
            animation: 'pm-spin 0.7s linear infinite',
            display: 'inline-block',
          }}
        />
      ) : (
        leadingIcon
      )}
      {children}
      {!loading && trailingIcon}
    </Tag>
  );
});

export default Button;

